import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { del, get, post, put, type CaptionsAPIKey, type CaptionsProvider, type CaptionsWorkerInfo, type CostAccount, type CostAccountInput, type CostDay, type CostKind, type CostProvider, type CostState, type CostsInfo } from '../../api'
import { Button, Field } from '../../components/ui'
import type { Spec } from './states'
import { ErrorBox, NetPanel, SkeletonRows, StateBadge, StateText } from './ui'

/*
 * Network › Captions › Cost accounts (owner 21.10, docs/captions.md «Учёт
 * затрат»): what the captions nodes cost us — not the users' billing. An
 * account is an external recognition service paid per hour of audio (its
 * API keys point at it) or a machine paid per hour connected (a worker
 * points at it). Today, the month, the pace to the month's end, a monthly
 * budget that warns or stops new jobs for its nodes. Amounts are estimates
 * from the center's meter, days and months UTC.
 */

export const COSTS_KEY = ['admin-costs']

export const COST_PROVIDERS: Record<CostProvider, string> = {
  assemblyai: 'AssemblyAI',
  soniox: 'Soniox',
  deepgram: 'Deepgram',
  openai: 'OpenAI',
  vultr: 'Vultr',
  scaleway: 'Scaleway',
  other: 'Other',
}
const API_PROVIDERS: CostProvider[] = ['assemblyai', 'soniox', 'deepgram', 'openai']
const MACHINE_PROVIDERS: CostProvider[] = ['vultr', 'scaleway', 'other']
const UNIT: Record<CostKind, string> = { api: 'h of audio', machine: 'machine h' }

const STATE: Record<CostState, Spec> = {
  ok: { glyph: '●', word: 'OK', color: 'live', border: 'solid', hint: 'Within the budget' },
  warn: { glyph: '▲', word: 'Near budget', color: 'warn', border: 'dashed', hint: 'Past 80 % of the monthly budget, or the month’s pace goes over it' },
  over: { glyph: '▲', word: 'Over budget', color: 'warn', border: 'solid', hint: 'Over the monthly budget; action: warn only' },
  stopped: { glyph: '■', word: 'Stopped', color: 'fail', border: 'solid', hint: 'Over the monthly budget: its nodes get no new jobs (running ones stay)' },
}

const SEL = 'min-h-9 rounded-md border border-line-2 bg-bg px-2 text-[13px] text-text-soft focus-visible:border-accent max-md:min-h-[44px]'

/** millionths of a dollar → "$12.40"; a cent and less: "<$0.01". */
export function usd(micros: number | null | undefined): string {
  if (micros == null) return '—'
  if (micros === 0) return '$0'
  const d = micros / 1e6
  if (d < 0.01) return '<$0.01'
  return `$${d >= 1000 ? Math.round(d).toLocaleString('en-US') : d.toFixed(2)}`
}
/** a price per hour: up to 4 decimals, trailing zeros off ("$0.462"). */
export function price(micros: number): string {
  return `$${(micros / 1e6).toFixed(4).replace(/\.?0+$/, '')}`
}
/** seconds → "3.2 h" / "45 min". */
export function hours(s: number): string {
  if (s < 60) return s > 0 ? '<1 min' : '0'
  if (s < 3600) return `${Math.round(s / 60)} min`
  return `${(s / 3600).toFixed(s >= 36000 ? 0 : 1)} h`
}
/** "$1.25" → micros; null for empty or not a number. */
export function parseUSD(v: string): number | null {
  const t = v.trim().replace(/^\$/, '')
  if (!t || !/^\d+(\.\d{0,6})?$/.test(t)) return null
  return Math.round(Number(t) * 1e6)
}

export function useCosts() {
  return useQuery({ queryKey: COSTS_KEY, queryFn: () => get<CostsInfo>('/api/admin/costs'), refetchInterval: 30_000 })
}

/** Cost of a node in the table: the month, today under it. */
export function WorkerCostCell({ w, testId }: { w: CaptionsWorkerInfo; testId?: string }) {
  if (!w.cost) return <span className="text-muted" data-testid={testId} title="No cost account, or nothing counted this month">—</span>
  return (
    <div className="tabular flex flex-col font-mono text-[12px] leading-5 whitespace-nowrap" data-testid={testId} title="This month (UTC), all its accounts · today">
      <span className="text-text">{usd(w.cost.month_micros)}</span>
      <span className="text-muted">today {usd(w.cost.today_micros)}</span>
    </div>
  )
}

/** The account select of a key (api accounts of its service) or of a worker (machine accounts). */
export function AccountSelect({ accounts, kind, provider, value, label, testId, onChange }: {
  accounts: CostAccount[]
  kind: CostKind
  provider?: CaptionsProvider
  value?: string
  label: string
  testId?: string
  onChange: (id: string | null) => Promise<void>
}) {
  const [busy, setBusy] = useState(false)
  const fit = accounts.filter((a) => a.kind === kind && (!provider || a.provider === provider))
  return (
    <select aria-label={label} data-testid={testId} className={SEL} disabled={busy} value={value ?? ''}
      onChange={async (e) => {
        setBusy(true)
        try {
          await onChange(e.target.value || null)
        } finally {
          setBusy(false)
        }
      }}>
      <option value="">No cost account</option>
      {fit.map((a) => <option key={a.id} value={a.id}>{a.name} · {price(a.price_micros)}/{UNIT[a.kind]}</option>)}
    </select>
  )
}

/** "Cost" in the details of a node: its machine account, and for an API node the key's account. */
export function WorkerCostBlock({ w, keys, accounts, onAssign }: { w: CaptionsWorkerInfo; keys: CaptionsAPIKey[]; accounts: CostAccount[]; onAssign: (id: string | null) => Promise<void> }) {
  const key = keys.find((k) => k.id === w.api_key_id)
  const keyAcc = accounts.find((a) => a.id === key?.account_id)
  return (
    <div className="flex flex-col gap-2 rounded-md border border-line p-3" data-testid={`worker-cost-${w.id}`}>
      <div className="flex flex-wrap items-center gap-3">
        <h3 className="mono-label uppercase">Cost</h3>
        <span className="tabular font-mono text-[13px]" data-testid={`worker-cost-month-${w.id}`}>
          {w.cost ? `${usd(w.cost.month_micros)} this month · ${usd(w.cost.today_micros)} today` : 'Nothing counted this month'}
        </span>
        {w.cost_stopped && <StateText spec={STATE.stopped} text="Budget stop: no new jobs" testId={`worker-cost-stopped-${w.id}`} />}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[13px]">
        <label className="flex items-center gap-2">
          <span className="text-muted">Machine hours</span>
          <AccountSelect accounts={accounts} kind="machine" value={w.cost_account_id} label={`Machine cost account of ${w.name}`} testId={`worker-account-${w.id}`} onChange={onAssign} />
        </label>
        {w.kind === 'api' && (
          <span className="text-muted" data-testid={`worker-key-account-${w.id}`}>
            Audio: {key ? (keyAcc ? <span className="text-text-soft">{keyAcc.name} (through key {key.label})</span> : `key ${key.label} has no cost account`) : 'no key'}
          </span>
        )}
      </div>
    </div>
  )
}

/** A month bar: spent of the budget, the pace as a tick. */
function BudgetBar({ a }: { a: CostAccount }) {
  if (a.budget_micros == null) return null
  const b = a.budget_micros
  const used = Math.min(100, (a.month_micros / b) * 100)
  const pace = a.forecast_micros != null ? Math.min(100, (a.forecast_micros / b) * 100) : null
  const c = a.state === 'stopped' ? 'bg-fail' : a.state === 'ok' ? 'bg-live' : 'bg-warn'
  return (
    <span className="relative mt-1 block h-1.5 w-full max-w-40 overflow-hidden rounded-full bg-line" aria-hidden>
      <span className={`block h-full ${c}`} style={{ width: `${Math.max(1, used)}%` }} />
      {pace != null && <span className="absolute top-0 h-full w-0.5 bg-text-soft" style={{ left: `calc(${pace}% - 1px)` }} />}
    </span>
  )
}

function DailyBars({ id }: { id: string }) {
  const q = useQuery({ queryKey: [...COSTS_KEY, 'daily', id], queryFn: () => get<CostDay[]>(`/api/admin/costs/accounts/${id}/daily?days=31`) })
  if (q.isPending) return <SkeletonRows rows={1} cols={4} />
  if (q.error) return <ErrorBox text={(q.error as Error).message} onRetry={() => void q.refetch()} busy={q.isFetching} />
  const days = q.data ?? []
  const max = Math.max(...days.map((d) => d.micros), 1)
  const total = days.reduce((n, d) => n + d.micros, 0)
  return (
    <figure className="flex flex-col gap-1" data-testid={`cost-daily-${id}`}>
      <figcaption className="flex items-baseline justify-between text-xs text-muted">
        <span>Last 31 days (UTC)</span>
        <span className="tabular font-mono">{usd(total)}</span>
      </figcaption>
      <div className="flex h-16 items-end gap-px" role="img" aria-label={`Daily cost, last 31 days: ${usd(total)} in all`}>
        {days.map((d) => (
          <span key={d.day} title={`${d.day}: ${usd(d.micros)} · ${hours(d.seconds)}`} className="min-w-0 flex-1 rounded-t-[1px] bg-accent/70"
            style={{ height: `${d.micros > 0 ? Math.max(4, (d.micros / max) * 100) : 0}%` }} />
        ))}
      </div>
      <div className="flex justify-between font-mono text-[10px] text-muted">
        <span>{days[0]?.day.slice(5)}</span>
        <span>{days[days.length - 1]?.day.slice(5)}</span>
      </div>
    </figure>
  )
}

type Form = { name: string; kind: CostKind; provider: CostProvider; price: string; budget: string; over: 'warn' | 'stop' }
const blank: Form = { name: '', kind: 'api', provider: 'assemblyai', price: '', budget: '', over: 'warn' }
const formOf = (a: CostAccount): Form => ({
  name: a.name, kind: a.kind, provider: a.provider, price: String(a.price_micros / 1e6), budget: a.budget_micros != null ? String(a.budget_micros / 1e6) : '', over: a.over_budget,
})

function AccountForm({ init, create, onSave, onCancel, testId }: { init: Form; create: boolean; onSave: (f: CostAccountInput) => Promise<void>; onCancel: () => void; testId: string }) {
  const [f, setF] = useState(init)
  const [busy, setBusy] = useState(false)
  const p = parseUSD(f.price)
  const b = f.budget.trim() ? parseUSD(f.budget) : null
  const ok = f.name.trim() && p != null && (!f.budget.trim() || (b != null && b > 0))
  const providers = f.kind === 'api' ? API_PROVIDERS : MACHINE_PROVIDERS
  return (
    <form
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
      data-testid={testId}
      onSubmit={async (e) => {
        e.preventDefault()
        if (!ok) return
        setBusy(true)
        try {
          await onSave({ name: f.name.trim(), ...(create ? { kind: f.kind, provider: f.provider } : {}), price_micros: p!, budget_micros: b, over_budget: f.over })
        } finally {
          setBusy(false)
        }
      }}
    >
      <Field label="Name" name="cost-name" value={f.name} maxLength={64} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Deepgram, main card" />
      {create && (
        <>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Pays for</span>
            <select name="cost-kind" className={`${SEL} w-full`} value={f.kind}
              onChange={(e) => {
                const kind = e.target.value as CostKind
                setF({ ...f, kind, provider: kind === 'api' ? 'assemblyai' : 'vultr' })
              }}>
              <option value="api">Recognition service: per hour of audio</option>
              <option value="machine">Machines: per hour a node is connected</option>
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium">Provider</span>
            <select name="cost-provider" className={`${SEL} w-full`} value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value as CostProvider })}>
              {providers.map((x) => <option key={x} value={x}>{COST_PROVIDERS[x]}</option>)}
            </select>
          </label>
        </>
      )}
      <Field label={`Price, $ per ${UNIT[f.kind]}`} name="cost-price" inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} placeholder="0.15"
        error={f.price && p == null ? 'A number of dollars, e.g. 0.462' : undefined} />
      <Field label="Monthly budget, $ (optional)" name="cost-budget" inputMode="decimal" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} placeholder="No budget"
        error={f.budget.trim() && (b == null || b <= 0) ? 'A positive number of dollars, or empty' : undefined} />
      <fieldset className="flex flex-col gap-1">
        <legend className="mb-1 text-sm font-medium">Over the budget</legend>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="cost-over" checked={f.over === 'warn'} onChange={() => setF({ ...f, over: 'warn' })} /> Warn only
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="cost-over" checked={f.over === 'stop'} onChange={() => setF({ ...f, over: 'stop' })} /> Stop new jobs on its nodes
        </label>
      </fieldset>
      <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3">
        <Button variant="primary" type="submit" disabled={!ok || busy} className="max-md:min-h-[44px]">{create ? 'Add account' : 'Save'}</Button>
        <Button variant="quiet" type="button" onClick={onCancel}>Cancel</Button>
        <span className="text-xs text-muted">Amounts are the center’s estimate, not the provider’s invoice. A new price counts from now on.</span>
      </div>
    </form>
  )
}

function bound(a: CostAccount, keys: CaptionsAPIKey[], workers: CaptionsWorkerInfo[]): ReactNode {
  const k = a.keys.map((id) => keys.find((x) => x.id === id)?.label ?? id)
  const w = a.workers.map((id) => workers.find((x) => x.id === id)?.name ?? id)
  const parts = [...(k.length ? [`${k.length === 1 ? 'key' : `${k.length} keys`}: ${k.join(', ')}`] : []), ...(w.length ? [`${w.length === 1 ? 'node' : `${w.length} nodes`}: ${w.join(', ')}`] : [])]
  return parts.length ? parts.join(' · ') : <span className="text-muted">Nothing bound</span>
}

export function CostAccountsPanel({ costs, keys, workers, onChanged }: {
  costs: ReturnType<typeof useCosts>
  keys: CaptionsAPIKey[]
  workers: CaptionsWorkerInfo[]
  onChanged: () => Promise<unknown>
}) {
  const [adding, setAdding] = useState(false)
  const [open, setOpen] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [err, setErr] = useState('')
  const list = costs.data?.accounts ?? []
  const month = list.reduce((n, a) => n + a.month_micros, 0)
  const run = async (f: () => Promise<unknown>) => {
    setErr('')
    try {
      await f()
      await onChanged()
      return true
    } catch (e) {
      setErr((e as Error).message)
      return false
    }
  }
  return (
    <NetPanel
      pad={false}
      title="Cost accounts"
      testId="cost-accounts"
      aside={
        <span className="flex items-center gap-3">
          {list.length > 0 && <span className="tabular font-mono text-xs text-muted" data-testid="cost-month-total" title="All accounts, this month (UTC)">{usd(month)} this month</span>}
          <Button variant="plain" data-testid="cost-add" aria-expanded={adding} className="max-md:min-h-[44px]" onClick={() => setAdding((a) => !a)}>{adding ? 'Close' : 'Add account'}</Button>
        </span>
      }
    >
      {err && <div className="px-4 pt-3"><ErrorBox text={err} /></div>}
      {costs.error && <div className="px-4 pt-3"><ErrorBox text={(costs.error as Error).message} onRetry={() => void costs.refetch()} busy={costs.isFetching} /></div>}
      {adding && (
        <div className="border-b border-line p-4">
          <AccountForm init={blank} create testId="cost-form-new" onCancel={() => setAdding(false)}
            onSave={async (f) => {
              if (await run(() => post('/api/admin/costs/accounts', f))) setAdding(false)
            }} />
        </div>
      )}
      {costs.isPending ? (
        <div className="p-4"><SkeletonRows rows={2} cols={5} /></div>
      ) : list.length === 0 ? (
        <p className="p-4 text-sm text-muted" data-testid="cost-empty">
          No cost accounts. Add one per service or machine provider, then pick it for an API key or a node: the center counts the audio sent to the service and the hours a node is connected.
        </p>
      ) : (
        <ul className="divide-y divide-line" data-testid="cost-list">
          {list.map((a) => (
            <li key={a.id} data-testid={`cost-${a.id}`} data-state={a.state} className="flex flex-col gap-3 px-4 py-3">
              <div className="grid grid-cols-2 items-start gap-x-4 gap-y-2 text-sm md:grid-cols-[minmax(0,1.6fr)_1fr_1fr_minmax(0,1.4fr)_auto_auto]">
                <div className="col-span-2 min-w-0 md:col-span-1">
                  <p className="flex min-w-0 items-center gap-2">
                    <span className="truncate font-medium" title={a.name}>{a.name}</span>
                    <span className="flex-none rounded-sm border border-line-2 px-1 font-mono text-[10px] leading-4 font-medium tracking-[.06em] text-text-soft">{a.kind === 'api' ? 'API' : 'MACHINE'}</span>
                  </p>
                  <p className="truncate text-xs text-muted">{COST_PROVIDERS[a.provider]} · {price(a.price_micros)}/{UNIT[a.kind]}</p>
                </div>
                <div className="tabular font-mono text-[13px]" data-testid={`cost-today-${a.id}`}>
                  <span className="mono-label block uppercase md:hidden">Today</span>
                  {usd(a.today_micros)}
                  <span className="block text-[11px] text-muted">{hours(a.today_seconds)}</span>
                </div>
                <div className="tabular font-mono text-[13px]" data-testid={`cost-month-${a.id}`}>
                  <span className="mono-label block uppercase md:hidden">Month</span>
                  {usd(a.month_micros)}
                  {a.budget_micros != null && <span className="text-muted"> of {usd(a.budget_micros)}</span>}
                  <BudgetBar a={a} />
                  {a.forecast_micros != null && <span className="block text-[11px] text-muted" data-testid={`cost-forecast-${a.id}`}>→ {usd(a.forecast_micros)} by month end</span>}
                </div>
                <div className="col-span-2 min-w-0 truncate text-[13px] md:col-span-1" data-testid={`cost-bound-${a.id}`}>{bound(a, keys, workers)}</div>
                <div>
                  <StateBadge spec={STATE[a.state]} testId={`cost-state-${a.id}`}
                    text={a.state === 'stopped' ? 'Stopped' : a.budget_micros == null && a.state === 'ok' ? 'No budget' : undefined} />
                  {a.budget_micros != null && <span className="mt-1 block text-[11px] text-muted">{a.over_budget === 'stop' ? 'stops new jobs' : 'warns only'}</span>}
                </div>
                <div className="flex items-center gap-1 justify-self-end">
                  <Button variant="quiet" aria-expanded={open === a.id} data-testid={`cost-open-${a.id}`} onClick={() => { setOpen((o) => (o === a.id ? null : a.id)); setEditing(null) }}>
                    {open === a.id ? 'Hide' : 'Details'}
                  </Button>
                </div>
              </div>
              {open === a.id && (
                <div className="flex flex-col gap-4 border-t border-line pt-3" data-testid={`cost-details-${a.id}`}>
                  <DailyBars id={a.id} />
                  {editing === a.id ? (
                    <AccountForm init={formOf(a)} create={false} testId={`cost-form-${a.id}`} onCancel={() => setEditing(null)}
                      onSave={async (f) => {
                        if (await run(() => put(`/api/admin/costs/accounts/${a.id}`, f))) setEditing(null)
                      }} />
                  ) : (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button variant="plain" data-testid={`cost-edit-${a.id}`} onClick={() => setEditing(a.id)}>Edit price and budget</Button>
                      <Button variant="quiet" data-testid={`cost-delete-${a.id}`} disabled={a.keys.length + a.workers.length > 0}
                        title={a.keys.length + a.workers.length > 0 ? 'Keys or nodes use it: pick another account for them first' : 'Delete the account and its history'}
                        onClick={() => void run(() => del(`/api/admin/costs/accounts/${a.id}`))}>
                        Delete
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </NetPanel>
  )
}
